import {
	createCanvasTimelineDate,
	DAY_NAMES,
	getCombinedTaskMode,
	getHolidayCalendar,
	getVisualizationMode,
	isTimeOffAllocation,
	VISUALIZATION_MODE,
} from '../canvas-timeline/canvas_timeline_util';
import {getWeekDayIndexFromCanvasDate} from '../canvas-timeline/canvas-timeline-date-util';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {isFullDayOff} from './workingDayUtils';
import SortedSet from 'sorted-set';
import DataManager from '../DataManager';
import {getChangeListEntities} from '../placeholders-scheduling/CanvasPlaceholdersSchedulingUtil';
import {STAFFING_CHANGE_LIST_ENTITIES} from '../placeholders-scheduling/CanvasPlaceholderSchedulingConstants';
import {clearPersonWeekdaysCache} from '../../scheduling/project_allocation_logic';

type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type PersonId = number | string;

// The subset of the `sorted-set` package's API this module uses. Dates are canvas timeline dates.
interface SortedDateSet {
	add(date: number): void;
	has(date: number): boolean;
	get(date: number): number | undefined;
	range(start: number, end: number): number[];
}

type NonWorkingDaysMap = Map<PersonId, SortedDateSet>;

type WorkingMinutesPerDay = Record<DayName, number>;

interface TimeOffAllocation extends Partial<Record<DayName, number>> {
	startYear: number;
	startMonth: number;
	startDay: number;
	endYear: number;
	endMonth: number;
	endDay: number;
}

interface SchedulingAllocation extends TimeOffAllocation {
	id: PersonId;
	personId?: PersonId;
}

interface SchedulingPerson extends WorkingMinutesPerDay {
	id: PersonId;
}

interface CalendarEntry {
	year: number;
	month: number;
	day: number;
}

export interface NonWorkingDaysFilterData {
	company: unknown;
	// Read by getHolidayCalendar to resolve the person's calendar.
	holidayCalendarMap: Map<unknown, unknown>;
	holidayCalendars: unknown[];
	holidayCalendarEntriesByCalendar: Record<string, CalendarEntry[] | undefined>;
	nonWorkingDaysMap?: NonWorkingDaysMap;
	personMap?: Map<PersonId, SchedulingPerson>;
	persons: SchedulingPerson[];
}

interface NonWorkingDaysPageComponent {
	state: {todayDate: number | null; schedulingOptions: unknown; staffingModeActive: boolean};
	getFilterData: () => NonWorkingDaysFilterData;
}

const dayNameOf = (canvasTimelineDate: number): DayName =>
	DAY_NAMES[getWeekDayIndexFromCanvasDate(canvasTimelineDate)] as DayName;

export const getSortedSetRange = (sortedSet: SortedDateSet, start: number, end: number): number[] => {
	return sortedSet.range(start, end);
};

export const getRange = (set: Iterable<number>, start: number, end: number): number[] => {
	const range: number[] = [];
	for (const entry of set) {
		if (entry >= start && entry <= end) {
			range.push(entry);
		}
	}

	return range;
};

export const isWorkingDay = (personWorkingMinutes: number[], canvasTimelineDate: number): boolean => {
	const weekdayIndex = getWeekDayIndexFromCanvasDate(canvasTimelineDate);
	return personWorkingMinutes[weekdayIndex] > 0;
};

export const getNonWorkingDays = (
	start: number,
	end: number,
	nonWorkingDaysMap: NonWorkingDaysMap | null | undefined,
	personId: PersonId | null | undefined,
	personWorkingMinutes?: number[]
): Set<number> => {
	const range = new Set<number>();

	if (!hasFeatureFlag('inverted_pto_non_working_days')) {
		const personNonWorkingDays = nonWorkingDaysMap && personId ? nonWorkingDaysMap.get(personId) : null;

		if (personNonWorkingDays) {
			// A day the person never works must not be counted as a day off. Callers subtract this
			// count from a tally of that person's *working* days, so counting a holiday that lands on a
			// day they never work over-subtracts, and for someone working few days a week it can drive
			// the count to zero and wipe out their task demand for the whole month.
			// The filter is a correctness rule rather than an optimisation, so it applies whenever the
			// caller supplies personWorkingMinutes. Callers that omit it keep the unfiltered count.
			//
			// The filter deliberately stops at the count. getNonWorkingDaysDistribution below calls
			// this with no personWorkingMinutes -- it has none in scope -- so the per-weekday
			// distribution still counts holidays on days the person never works, and since this fix
			// it no longer agrees with getNonWorkingDaysCount. That divergence is safe, not an
			// oversight. On the heatmap path the distribution is subtracted per weekday inside
			// getWeekdaysBetweenDatesNew, and every reduce over that result gates each index on
			// personWorkingMinutes[index], so an over-subtraction on a day the person never works is
			// multiplied by zero and cannot reach a total. project_allocation_logic.js's use is a
			// pre-existing path that never passed personWorkingMinutes either, so its behaviour is
			// unchanged here. Only the count is subtracted from a tally of the person's working
			// days, so only the count could over-subtract -- which is the defect this filter fixes.
			// Threading personWorkingMinutes through the distribution's callers would be a wider
			// change with no behavioural payoff.
			const countsAsDayOff = (date: number) => !personWorkingMinutes || isWorkingDay(personWorkingMinutes, date);

			// If start and end is same date, just do a direct lookup in the Set
			if (start === end) {
				const result = personNonWorkingDays.get(start);

				if (result && countsAsDayOff(result)) {
					range.add(result);
				}
			} else if (start < end) {
				getSortedSetRange(personNonWorkingDays, start, end).forEach(result => {
					if (countsAsDayOff(result)) {
						range.add(result);
					}
				});
			}
		}
	}
	return range;
};

export const getNonWorkingDaysDistribution = (
	start: number,
	end: number,
	nonWorkingDaysMap: NonWorkingDaysMap | null | undefined,
	personId: PersonId | null | undefined
): number[] => {
	const nonWorkingDaysDistribution = [0, 0, 0, 0, 0, 0, 0];

	if (nonWorkingDaysMap && personId && !hasFeatureFlag('inverted_pto_non_working_days')) {
		const range = getNonWorkingDays(start, end, nonWorkingDaysMap, personId);

		for (const dayEntry of range) {
			if (dayEntry >= start && dayEntry <= end) {
				const weekIndex = getWeekDayIndexFromCanvasDate(dayEntry);
				nonWorkingDaysDistribution[weekIndex]++;
			}
		}
	}

	return nonWorkingDaysDistribution;
};

export const getNonWorkingDaysCount = (
	start: number,
	end: number,
	nonWorkingDaysMap: NonWorkingDaysMap | null | undefined,
	personId: PersonId | null | undefined,
	personWorkingMinutes?: number[]
): number => {
	return getNonWorkingDays(start, end, nonWorkingDaysMap, personId, personWorkingMinutes).size;
};

// Time off is summed per day across all of the person's time-off allocations, so several partial
// entries that together cover the working day make it a day off.
export const getTimeOffDaysOff = (
	person: WorkingMinutesPerDay,
	timeOffAllocations: TimeOffAllocation[],
	todayDate: number | null
): Set<number> => {
	const timeOffMinutesByDay = new Map<number, number>();

	for (const allocation of timeOffAllocations) {
		const canvasStartDate = createCanvasTimelineDate(allocation.startYear, allocation.startMonth, allocation.startDay);
		const canvasEndDate = createCanvasTimelineDate(allocation.endYear, allocation.endMonth, allocation.endDay);

		for (let day = canvasStartDate; day <= canvasEndDate; day++) {
			if (todayDate === null || day >= todayDate) {
				const dayName = dayNameOf(day);
				timeOffMinutesByDay.set(day, (timeOffMinutesByDay.get(day) || 0) + (allocation[dayName] || 0));
			}
		}
	}

	const daysOff = new Set<number>();
	for (const [day, timeOffMinutes] of timeOffMinutesByDay) {
		if (isFullDayOff(person[dayNameOf(day)], timeOffMinutes)) {
			daysOff.add(day);
		}
	}

	return daysOff;
};

const calculateForPerson = (
	pageComponent: NonWorkingDaysPageComponent,
	person: SchedulingPerson,
	data: NonWorkingDaysFilterData,
	todayDate: number | null
): SortedDateSet => {
	const {schedulingOptions, staffingModeActive} = pageComponent.state;

	const personDatesSet: SortedDateSet = new SortedSet();
	const {company, holidayCalendarEntriesByCalendar} = data;
	const inCombinationActualMode =
		getVisualizationMode(schedulingOptions, company, VISUALIZATION_MODE.COMBINATION) &&
		getCombinedTaskMode(company) === VISUALIZATION_MODE.TASK_ACTUAL;
	const inActualMode =
		getVisualizationMode(schedulingOptions, company, VISUALIZATION_MODE.TASK_ACTUAL) || inCombinationActualMode;
	const holidayCalendar = getHolidayCalendar(person, data);
	const calendarEntries = holidayCalendar ? holidayCalendarEntriesByCalendar[holidayCalendar.id] : [];

	// Calendar Entries Loop
	if (calendarEntries) {
		for (let calendarEntryIndex = 0; calendarEntryIndex < calendarEntries.length; calendarEntryIndex++) {
			const calEntry = calendarEntries[calendarEntryIndex];
			const day = createCanvasTimelineDate(calEntry.year, calEntry.month, calEntry.day);
			personDatesSet.add(day);
		}
	}

	// Time Off Allocation Loop
	const calculationTodayDate = inActualMode ? todayDate : null;
	const personAllocations: SchedulingAllocation[] = DataManager.getAllocationsByPersonId(pageComponent, person.id);
	const changedPersonAllocationIds = new Set<PersonId>();
	const timeOffAllocations: TimeOffAllocation[] = [];

	if (staffingModeActive) {
		const changedAllocations: SchedulingAllocation[] = getChangeListEntities(
			pageComponent,
			STAFFING_CHANGE_LIST_ENTITIES.PROJECT_ALLOCATIONS_CREATE_UPDATE
		);

		for (const changedAllocation of changedAllocations) {
			const {personId} = changedAllocation;

			if (personId && personId === person.id && isTimeOffAllocation(changedAllocation)) {
				changedPersonAllocationIds.add(changedAllocation.id);
				timeOffAllocations.push(changedAllocation);
			}
		}
	}

	if (personAllocations) {
		for (let allocationIndex = 0; allocationIndex < personAllocations.length; allocationIndex++) {
			const allocation = personAllocations[allocationIndex];

			if (isTimeOffAllocation(allocation) && !changedPersonAllocationIds.has(allocation.id)) {
				timeOffAllocations.push(allocation);
			}
		}
	}

	for (const day of getTimeOffDaysOff(person, timeOffAllocations, calculationTodayDate)) {
		personDatesSet.add(day);
	}

	return personDatesSet;
};

export const calculateNonWorkingDaysMap = (pageComponent: NonWorkingDaysPageComponent, personId: PersonId | null): void => {
	const data = pageComponent.getFilterData();
	const {todayDate} = pageComponent.state;
	const nonWorkingDaysMap: NonWorkingDaysMap = data.nonWorkingDaysMap ? data.nonWorkingDaysMap : new Map();
	const person = personId === null ? undefined : data.personMap?.get(personId);

	// If a single person was found from personId, only update for that person
	// instead of all persons
	const persons = person ? [person] : data.persons;
	for (let personIndex = 0; personIndex < persons.length; personIndex++) {
		const person = persons[personIndex];
		const personDatesSet = calculateForPerson(pageComponent, person, data, todayDate);
		nonWorkingDaysMap.set(person.id, personDatesSet);
	}

	data.nonWorkingDaysMap = nonWorkingDaysMap;

	if (hasFeatureFlag('combined_mode_performance_improvements')) {
		clearPersonWeekdaysCache(personId);
	}
};

export const isPersonDayOff = (
	date: number,
	personId: PersonId,
	nonWorkingDaysMap: NonWorkingDaysMap | null | undefined
): boolean | undefined => {
	return nonWorkingDaysMap ? nonWorkingDaysMap.get(personId)?.has(date) : false;
};
